import { BaseApiService } from './BaseApiService';
import { DateRange } from './DashboardService';

/** What one distribution center paid for an item over a window (or one week). */
export interface DcPriceCell {
  price: number; // spend / cases
  quantity: number; // cases
  spend: number;
  storeCount: number;
  minStorePrice: number; // lowest per-store price inside the DC
  maxStorePrice: number; // highest per-store price inside the DC
}

export interface DcPriceItem {
  itemNumber: string | null; // GFS Material ID (null for rows imported before item numbers existed)
  description: string;
  packSize: string | null;
  prices: Record<string, DcPriceCell>; // DC name -> cell (only DCs that bought the item)
  lowestDc: string | null;
  lowestPrice: number | null;
  highestDc: string | null;
  highestPrice: number | null;
  spreadAmount: number | null; // highest - lowest, null when < 2 DCs
  spreadPct: number | null;
  totalQuantity: number;
  totalSpend: number;
  potentialSavings: number; // if every DC had paid the lowest DC price
  dcCount: number;
}

export interface DcPriceData {
  startDate: string;
  endDate: string;
  weeksInRange: number;
  dcs: string[]; // column order
  dcStores: Record<string, string[]>; // DC -> stores it serves (includes "Unassigned")
  unassignedStoresWithPurchases: string[];
  itemCount: number;
  itemsWithDifferences: number;
  totalSpend: number;
  totalPotentialSavings: number;
  items: DcPriceItem[];
}

export interface DcPriceWeek {
  weekStart: string;
  weekEnding: string;
  prices: Record<string, DcPriceCell>;
  lowestDc: string | null;
  lowestPrice: number | null;
  spreadAmount: number | null;
}

export interface DcPriceHistory {
  itemNumber: string | null;
  description: string | null;
  packSize: string | null;
  dcs: string[];
  weeks: DcPriceWeek[]; // most recent first
}

/** Stable identity for an item row: the GFS item number, or the description for legacy rows. */
export const dcPriceItemKey = (item: { itemNumber: string | null; description: string }): string =>
  item.itemNumber && item.itemNumber.trim() ? item.itemNumber.trim() : `desc:${item.description}`;

export class DcPriceService extends BaseApiService {
  private static instance: DcPriceService;

  private constructor() {
    super();
  }

  public static getInstance(): DcPriceService {
    if (!DcPriceService.instance) {
      DcPriceService.instance = new DcPriceService();
    }
    return DcPriceService.instance;
  }

  async getDcPrices(startDate: string, endDate: string): Promise<DcPriceData> {
    return this.get<DcPriceData>(`/api/dc-prices/data?startDate=${startDate}&endDate=${endDate}`, undefined, true);
  }

  async getItemHistory(itemKey: string): Promise<DcPriceHistory> {
    return this.get<DcPriceHistory>(
      `/api/dc-prices/item-history?itemKey=${encodeURIComponent(itemKey)}`,
      undefined,
      true
    );
  }

  // Weekly (Mon–Sun) ranges with GFS data, newest first.
  async getWeeks(): Promise<DateRange[]> {
    return this.get<DateRange[]>('/api/dc-prices/weeks', undefined, true);
  }

  async getAssignments(): Promise<Record<string, string[]>> {
    return this.get<Record<string, string[]>>('/api/dc-prices/assignments', undefined, true);
  }
}

export default DcPriceService.getInstance();
